import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import type { ReactElement } from "react";
import { translateForLocale, useLocale, useTranslations } from "@/lib/i18n/intl";
import { locales } from "@/lib/i18n/config";
import { getLocaleDirection, keys } from "@/lib/i18n/portal";
import { getSellerPath } from "@/lib/routing/portal-urls";
import { invalidateSellerSession } from "@/lib/query/core/invalidation";
import { useGovernoratesQuery } from "@/modules/settings/services/useGovernoratesQuery";
import { useStoreQuery } from "@/shared/services/useStoreQuery";
import {
  EMPTY_INSTA,
  type PanelForm,
} from "@/modules/settings/components/redesign/store-settings/payment/payment-form";
import { Icons, ic } from "./wizard-icons";
import {
  PRODUCT_TYPE_KEY,
  useWizardData,
  type CatPair,
  type ProductTypeId,
} from "./wizard-data";
import type { BrandColors, ShippingRow, ShippingState, WizardSnapshot } from "./wizard-types";
import { extractPalette, preselectAccent, derivePalette } from "./brand-colors";
import { clearSnapshot, loadSnapshot, saveSnapshot } from "./wizard-storage";
import { useQuickSetupSubmit, type SubmitInput } from "./useQuickSetupSubmit";
import { CategoriesStep } from "./components/CatalogSteps";
import { SellTypeStep } from "./components/SellTypeStep";
import { LogoUpload, WhatsAppStep, type Governorate } from "./components/InputSteps";
import { DeliveryStep } from "./components/DeliveryStep";
import { WizardPaymentStep, paymentSummary } from "./components/WizardPaymentStep";
import { QuestionScreen } from "./components/QuestionScreen";
import { SetupSidebar } from "./components/SetupSidebar";
import { MobileHeader } from "./components/MobileHeader";
import { Done, type SummaryRow } from "./components/DoneScreen";
import "./wizard.css";

const K = keys.quickSetup;

const DEFAULT_PAY: PanelForm = {
  cod: true,
  autoConfirmCod: false,
  walletOn: false,
  wallets: [],
  instaOn: false,
  insta: EMPTY_INSTA,
};

function useWindowWidth(): number {
  const [width, setWidth] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth : 1280,
  );
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return width;
}

type QuickSetupWizardProps = {
  locale: string;
  storeSlug: string;
};

export function QuickSetupWizard({ locale, storeSlug }: QuickSetupWizardProps): ReactElement {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const t = useTranslations();
  const activeLocale = useLocale();
  const dir = getLocaleDirection(activeLocale);

  // The wizard renders outside the AppShell, so it carries its own language
  // toggle — swap the locale segment of the current path and stay put.
  const switchLocale = () => {
    const next = activeLocale === "ar" ? "en" : "ar";
    const localePattern = new RegExp(`^/(${locales.join("|")})(/|$)`);
    const nextPath = localePattern.test(pathname)
      ? pathname.replace(localePattern, `/${next}$2`)
      : `/${next}${pathname.startsWith("/") ? "" : "/"}${pathname}`;
    navigate(nextPath, { replace: true });
  };
  const width = useWindowWidth();
  const narrow = width < 940;

  const { steps, phases } = useWizardData();

  const { data: governoratesData } = useGovernoratesQuery({ storeSlug });
  const { data: storeData } = useStoreQuery({ storeSlug });
  const storeName = storeData?.storeName?.trim() || t(K.brand.name);
  const governorates: Governorate[] = useMemo(
    () =>
      (governoratesData ?? [])
        .filter((g) => g.isActive)
        .map((g) => ({ id: g.id, name: activeLocale === "ar" ? g.nameAr : g.nameEn?.trim() || g.nameAr })),
    [governoratesData, activeLocale],
  );

  const submit = useQuickSetupSubmit(storeSlug, storeName);

  // ── State (restored from localStorage on first render) ──────────
  const [snap] = useState<WizardSnapshot | null>(() => loadSnapshot(storeSlug));

  const [step, setStep] = useState(snap?.step ?? 0);
  const [sellType, setSellType] = useState<ProductTypeId | null>(snap?.sellType ?? null);
  const [sellTypeOther, setSellTypeOther] = useState<string>(snap?.sellTypeOther ?? "");
  const [payForm, setPayForm] = useState<PanelForm>(snap?.payForm ?? DEFAULT_PAY);
  const [cats, setCats] = useState<CatPair[]>(snap?.cats ?? []);
  const [logo, setLogo] = useState<string | null>(snap?.logo ?? null);
  // Confirmed brand colors from the Logo step (Phase 3). Null until a colorful
  // logo is extracted or the seller taps neutral; a resumed snapshot restores it
  // so refreshing mid-step keeps the confirmation panel + accent choice.
  const [brandColors, setBrandColors] = useState<BrandColors | null>(snap?.brandColors ?? null);
  const [whats, setWhats] = useState(snap?.whats ?? "");
  // `shipping` is the v2 nationwide/custom union (wizard-types.ts). Default =
  // the nationwide preset (50 EGP / 3 days) per the locked "nationwide-first"
  // decision; a resumed snapshot keeps whichever mode the seller last had.
  const [shipping, setShipping] = useState<ShippingState>(
    snap?.shipping ?? { mode: "nationwide", price: "50", days: "3" },
  );
  const [done, setDone] = useState(snap?.done ?? false);
  const [persisted, setPersisted] = useState(snap?.persisted ?? false);
  // True while the Categories step is fetching AI suggestions — lifted here so
  // the QuestionScreen badge ✦ can spin in place (the handoff animates the star
  // inside the navy tile, not a separate element).
  const [suggestLoading, setSuggestLoading] = useState(false);

  const [description, setDescription] = useState(snap?.description ?? "");
  const [descriptionEn, setDescriptionEn] = useState(snap?.descriptionEn ?? "");

  const [descLoading, setDescLoading] = useState(false);
  const [descErr, setDescErr] = useState(false);
  // Guarded the same way as `persistStarted`: a hard reload of a snapshot that
  // already has a saved description (or was already `persisted`) must NOT
  // re-run `generateDescription` — avoids the "Trioty" placeholder flash (the
  // storeName fallback resolving before `useStoreQuery` settles) and a wasted
  // generate-endpoint call on every reload (Phase 4 gate NIT #2).
  const genStarted = useRef(Boolean(snap?.persisted) || Boolean(snap?.description?.trim()));
  const persistStarted = useRef(snap?.persisted ?? false);

  const cur = steps[step];

  // Map the current step to its top-level sidebar/mobile-header index: the
  // "products" and "categories" steps both map to phase index 0 ("Your
  // products"); everything else is 1:1 with a phase.
  const topLevelIndex = useMemo(() => {
    const idx = phases.findIndex((ph) => ph.steps.some((s) => s.idx === step));
    return idx === -1 ? 0 : idx;
  }, [phases, step]);

  // Done-screen "Categories" summary row: the actual selected category names,
  // each consistently resolved in the ACTIVE locale (never mixed ar/en), and
  // joined with " · " — not a bare count. A dash when none were added.
  const categoriesAnswer = useMemo(
    () => cats.map((c) => (activeLocale === "ar" ? c.ar : c.en)).join(" · "),
    [cats, activeLocale],
  );

  // The remaining free-text Done-screen summary answers are all DERIVED from
  // the raw answer state (never their own separate state) so a resumed
  // snapshot — which only restores the raw state, not a snapshot of these
  // strings — always renders the correct summary after a reload, including
  // when `done` is already true and the wizard controls never re-mount.
  const productsLabel = useMemo(
    () => (sellType === "other" ? sellTypeOther.trim() : sellType ? t(PRODUCT_TYPE_KEY[sellType]) : ""),
    [sellType, sellTypeOther, t],
  );
  // Per-language counterparts of `productsLabel` for the bilingual store
  // description/SEO templates (BUG S7): preset types have a translation in
  // both locales (`PRODUCT_TYPE_KEY`), resolved directly for each language
  // via `translateForLocale` regardless of the wizard's active locale. A
  // freeform "other" label is only known in whichever locale the seller
  // typed it in, so the other language is left blank — the fallback builder
  // substitutes a generic label instead of injecting the wrong language.
  const productsLabelEn = useMemo(
    () =>
      sellType === "other"
        ? activeLocale === "en"
          ? sellTypeOther.trim()
          : ""
        : sellType
          ? translateForLocale("en", PRODUCT_TYPE_KEY[sellType])
          : "",
    [sellType, sellTypeOther, activeLocale],
  );
  const productsLabelAr = useMemo(
    () =>
      sellType === "other"
        ? activeLocale === "ar"
          ? sellTypeOther.trim()
          : ""
        : sellType
          ? translateForLocale("ar", PRODUCT_TYPE_KEY[sellType])
          : "",
    [sellType, sellTypeOther, activeLocale],
  );
  const logoAnswer = logo ? t(K.summary.uploaded) : "";
  // Display-only formatting for the Done summary row (matches the design's
  // "+20 120 888 8474" pattern): strip the redundant leading 0 from the local
  // 01XXXXXXXXX number and group the remaining 10 digits 3-3-4. The
  // stored/normalized value (`whats`, persisted as-is) is untouched — this is
  // purely cosmetic display formatting.
  const whatsAnswer = useMemo(() => {
    const trimmed = whats.trim();
    if (!trimmed) return "";
    const digits = trimmed.replace(/[^0-9]/g, "");
    const local = digits.startsWith("0") ? digits.slice(1) : digits;
    if (local.length === 10) {
      return `+20 ${local.slice(0, 3)} ${local.slice(3, 6)} ${local.slice(6)}`;
    }
    return "+20 " + trimmed;
  }, [whats]);
  const paymentsAnswer = useMemo(() => paymentSummary(payForm, t, activeLocale), [payForm, t, activeLocale]);
  const shippingAnswer = useMemo(
    () =>
      shipping.mode === "nationwide"
        ? t(K.summary.nationwide, { price: shipping.price, days: shipping.days })
        : t(shipping.rows.length > 1 ? K.summary.areasMany : K.summary.areasOne, {
            count: shipping.rows.length,
          }),
    [shipping, t],
  );

  // ── Persist snapshot to localStorage on change ──────────────────
  useEffect(() => {
    const snapshot: WizardSnapshot = {
      step,
      answers: {
        products: productsLabel,
        logo: logoAnswer,
        whatsapp: whatsAnswer,
        payments: paymentsAnswer,
        categories: categoriesAnswer,
        shipping: shippingAnswer,
        description,
        descriptionEn,
      },
      logo,
      sellType,
      sellTypeOther,
      cats,
      payForm,
      whats,
      shipping,
      brandColors,
      done,
      description,
      descriptionEn,
      persisted,
    };
    saveSnapshot(storeSlug, snapshot);
  }, [
    storeSlug,
    step,
    productsLabel,
    logoAnswer,
    whatsAnswer,
    paymentsAnswer,
    categoriesAnswer,
    shippingAnswer,
    logo,
    sellType,
    sellTypeOther,
    cats,
    payForm,
    whats,
    shipping,
    brandColors,
    done,
    description,
    descriptionEn,
    persisted,
  ]);

  // ── Step flow ─────────────────────────────────────────────────────
  const goNext = (nextStep: number) => {
    if (nextStep >= steps.length) {
      setDone(true);
      return;
    }
    setStep(nextStep);
  };

  const finishLogo = () => {
    goNext(step + 1);
  };

  // ── Brand-color extraction + confirmation (Phase 3) ──────────────
  // Run client-side extraction after the preview paints. On a non-empty palette
  // set `brandColors` (extracted) so the confirmation panel appears; on an empty
  // result / failure clear it so the step behaves exactly as before (no panel).
  const runExtraction = useCallback(async (dataUrl: string, filename: string) => {
    try {
      const swatches = await extractPalette(dataUrl);
      const palette = swatches.map((s) => s.hex);
      if (palette.length === 0) {
        setBrandColors(null);
        return;
      }
      const accent = preselectAccent(palette);
      setBrandColors({
        theme: "extracted",
        palette,
        accent,
        derived: derivePalette(accent, palette),
        filename,
      });
    } catch {
      // Extraction failed — no panel, upload/skip flow stays unchanged.
      setBrandColors(null);
    }
  }, []);

  // Tap a swatch → re-select accent + recompute the derived tokens so the mini
  // preview re-renders. Also flips a neutral choice back to extracted.
  const selectAccent = (hex: string) =>
    setBrandColors((bc) =>
      bc
        ? { ...bc, theme: "extracted", accent: hex, derived: derivePalette(hex, bc.palette) }
        : bc,
    );

  // "Use neutral colors instead" → keep the palette (so the seller can toggle
  // back) but drop the accent/derived tokens and mark the theme neutral.
  const chooseNeutral = () =>
    setBrandColors((bc) => (bc ? { ...bc, theme: "neutral", accent: null, derived: null } : bc));

  // What do you sell? — single-select product type. Choosing "other" reveals
  // a required freeform label whose trimmed text becomes `productsLabel`
  // (never the literal "Something else") — derived above from `sellType`/
  // `sellTypeOther`, so nothing to stash here beyond advancing the step.
  const selectSellType = (id: ProductTypeId) => setSellType(id);
  const finishProducts = () => {
    goNext(step + 1);
  };

  const confirmWhats = () => {
    goNext(step + 1);
  };
  const skipWhats = () => {
    setWhats("");
    goNext(step + 1);
  };

  const confirmPayments = () => {
    goNext(step + 1);
  };

  const finishShipping = (state: ShippingState) => {
    setShipping(state);
    goNext(step + 1);
  };
  const finishCategories = (list: CatPair[]) => {
    setCats(list);
    goNext(step + 1);
  };

  // Back — revise the previous answer.
  const goBack = () => {
    if (done || step === 0) return;
    const prev = steps[step - 1];
    if (prev.id === "payments") setPayForm(DEFAULT_PAY);
    if (prev.id === "products") {
      setSellType(null);
      setSellTypeOther("");
    }
    if (prev.id === "categories") setCats([]);
    setStep(step - 1);
  };

  // The categories step's heading echoes the products answer (e.g. "Fashion &
  // clothing — nice! How should customers browse them?"); every other step
  // uses its own static question copy.
  const screenHeading =
    cur?.id === "categories" && productsLabel
      ? t(K.steps.categories.heading, { type: productsLabel })
      : (cur?.q ?? "");

  // Row order per the design handoff README: Sells, Categories, Logo,
  // WhatsApp, Payment, Shipping (Phase 4 gate NIT #4, folded in here).
  const summary: SummaryRow[] = [
    { label: t(K.summary.sells), value: productsLabel || t(K.summary.dash) },
    { label: t(K.summary.categories), value: categoriesAnswer || t(K.summary.dash) },
    { label: t(K.summary.logo), value: logoAnswer || t(K.summary.addLater) },
    // Force LTR on the phone-number value: "+20 120 888 8474" is space-grouped
    // digits, and the Unicode bidi algorithm reorders those groups when the
    // string sits inside an RTL (`ar`) paragraph without an explicit
    // direction — confirmed visually broken in the RTL sweep before this fix.
    { label: t(K.summary.whatsapp), value: whatsAnswer || t(K.summary.addLater), valueDir: "ltr" },
    { label: t(K.summary.payment), value: paymentsAnswer || t(K.summary.dash) },
    { label: t(K.summary.shipping), value: shippingAnswer || t(K.summary.dash) },
  ];

  // Expand the `ShippingState` answer into the flat `ShippingRow[]` shape
  // `useQuickSetupSubmit` (unchanged) already expects: "custom" passes its
  // rows through as-is; "nationwide" expands the single price/days preset
  // into a row for every active governorate, so `buildShippingRules` groups
  // them by price|days into exactly ONE rule covering all active governorate
  // ids — the persist contract (payloads/idempotency/grouping) is untouched,
  // only the *input* rows differ.
  const shippingRows: ShippingRow[] = useMemo(
    () =>
      shipping.mode === "custom"
        ? shipping.rows
        : governorates.map((g) => ({
            govId: g.id,
            gov: g.name,
            price: shipping.price,
            days: shipping.days,
          })),
    [shipping, governorates],
  );

  // ── Build the submit input from collected state ──────────────────
  const buildSubmitInput = useCallback(
    (): SubmitInput => ({
      logo,
      cats,
      productsLabel,
      productsLabelEn,
      productsLabelAr,
      payForm,
      shipping: shippingRows,
      whats,
      brandColors,
    }),
    [
      logo,
      cats,
      productsLabel,
      productsLabelEn,
      productsLabelAr,
      payForm,
      shippingRows,
      whats,
      brandColors,
    ],
  );

  // ── On completion: persist to backend + generate AI description ──
  const runPersist = useCallback(async () => {
    try {
      await submit.persist(buildSubmitInput());
      setPersisted(true);
      persistStarted.current = true;
    } catch {
      // error surfaced via submit.status / submit.error
    }
  }, [submit, buildSubmitInput]);

  const generateDescription = useCallback(async () => {
    setDescLoading(true);
    setDescErr(false);
    const { ar, en, isFallback } = await submit.generateDescription(buildSubmitInput());
    setDescription(ar);
    setDescriptionEn(en);
    setDescErr(isFallback);
    setDescLoading(false);
  }, [submit, buildSubmitInput]);

  useEffect(() => {
    if (!done) return;
    if (!genStarted.current) {
      genStarted.current = true;
      queueMicrotask(() => {
        void generateDescription();
      });
    }
    if (!persistStarted.current) {
      persistStarted.current = true;
      queueMicrotask(() => {
        void runPersist();
      });
    }
  }, [done, generateDescription, runPersist]);

  // Save the AI description, refresh the cached session overview (so the
  // dashboard sees the new categories/payments and doesn't bounce back into
  // setup), clear the local snapshot, then navigate.
  const finishAndGo = async (path: string) => {
    await submit.saveDescription(description, descriptionEn);
    await invalidateSellerSession(queryClient, storeSlug);
    clearSnapshot(storeSlug);
    navigate(getSellerPath(locale, storeSlug, path), { replace: true });
  };
  const goToDashboard = () => finishAndGo("/dashboard");
  const goToAddProducts = () => finishAndGo("/products");

  // ── Render current step's control inside the QuestionScreen slot ──
  const renderControl = (): ReactElement | null => {
    switch (cur?.type) {
      case "logo":
        return (
          <LogoUpload
            value={logo}
            onChange={setLogo}
            onDone={finishLogo}
            brandColors={brandColors}
            onExtract={runExtraction}
            onSelectAccent={selectAccent}
            onNeutral={chooseNeutral}
            narrow={narrow}
          />
        );
      case "producttypes":
        return cur.options ? (
          <SellTypeStep
            options={cur.options}
            selected={sellType}
            other={sellTypeOther}
            onSelect={selectSellType}
            onOtherChange={setSellTypeOther}
            onDone={finishProducts}
            narrow={narrow}
          />
        ) : null;
      case "whatsapp":
        return <WhatsAppStep value={whats} onChange={setWhats} onDone={confirmWhats} onSkip={skipWhats} />;
      case "payments":
        return (
          <WizardPaymentStep form={payForm} onChange={setPayForm} onDone={confirmPayments} minLabel={cur.minLabel} />
        );
      case "categories":
        return (
          <CategoriesStep
            sellType={sellType}
            sellTypeOther={sellTypeOther}
            initial={cats}
            onDone={finishCategories}
            onLoadingChange={setSuggestLoading}
          />
        );
      case "shipping":
        return (
          <DeliveryStep
            governorates={governorates}
            initial={shipping}
            onDone={finishShipping}
            narrow={narrow}
          />
        );
      default:
        return null;
    }
  };

  // ── Render ───────────────────────────────────────────────────────
  // Done is a full-screen navy takeover — it replaces the sidebar + question-
  // screen shell entirely (no sidebar, no mobile header, no locale pill row;
  // the mock's Done frames are their own self-contained composition).
  if (done) {
    return (
      <div
        className="qs-root"
        dir={dir}
        style={{ position: "fixed", inset: 0, overflowY: "auto", background: "var(--navy)" }}
      >
        <Done
          summary={summary}
          logo={logo}
          description={activeLocale === "ar" ? description : descriptionEn}
          onDescChange={activeLocale === "ar" ? setDescription : setDescriptionEn}
          descLoading={descLoading}
          descErr={descErr}
          onGoToDashboard={goToDashboard}
          onAddProducts={goToAddProducts}
          submitting={submit.status === "saving"}
          submitError={submit.status === "error" ? submit.error : null}
          onRetry={runPersist}
          narrow={narrow}
        />
      </div>
    );
  }

  return (
    <div
      className="qs-root"
      dir={dir}
      style={{ position: "fixed", inset: 0, display: "flex", background: "var(--bg)" }}
    >
      {!narrow && (
        <SetupSidebar
          topLevelIndex={topLevelIndex}
          done={done}
          phases={phases}
          storeName={storeName}
          logo={logo}
        />
      )}

      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        {narrow && (
          <MobileHeader
            topLevelIndex={topLevelIndex}
            done={done}
            phases={phases}
            onBack={goBack}
            showBack={step > 0}
            onSwitchLocale={switchLocale}
            activeLocale={activeLocale}
          />
        )}
        {!narrow && (
          <div
            style={{
              position: "absolute",
              top: 28,
              insetInlineEnd: 36,
              zIndex: 20,
            }}
          >
            <button
              onClick={switchLocale}
              aria-label={activeLocale === "ar" ? "English" : "العربية"}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                padding: "8px 16px",
                borderRadius: 999,
                background: "var(--surface)",
                border: "1px solid var(--border-strong)",
                color: "var(--navy)",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              <span style={{ display: "flex", color: "var(--muted)" }}>{ic(Icons.globe, 15)}</span>
              {activeLocale === "ar" ? "English" : "العربية"}
            </button>
          </div>
        )}
        <QuestionScreen
          heading={screenHeading}
          sub={cur?.sub ?? ""}
          onBack={goBack}
          showBack={step > 0}
          narrow={narrow}
          loading={suggestLoading}
          loadingSub={t(K.categories.suggesting)}
        >
          {renderControl()}
        </QuestionScreen>
      </div>
    </div>
  );
}
